import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { Checkbox } from "@mui/material";
import * as React from "react";
import { Link } from "react-router";

import type { S3ObjectEntry } from "@/shared/domain/integrations";
import {
  cellStyles,
  createColumnHelper,
  DataTable,
  renderDashOrValue,
  type ColDef,
  type DataTableLoadingState,
  type DataTableState,
  type ICellRendererParams,
  type ValueFormatterParams,
} from "@/shared/foundation/primitives/Table";
import { Goto } from "@/shared/navigation/Goto";
import { formatByteSize, truncateMiddle } from "@/shared/text";

import styles from "./LevelTable.module.css";
import { entryDisplayName } from "./prefixPath";

const columnHelper = createColumnHelper<S3ObjectEntry>();

// Long enough that a dated log path keeps both its leading folder and its extension.
const NAME_DISPLAY_LIMIT = 90;

// The grid's 16px cell padding on each side plus a small MUI checkbox's 38px hit target. Any
// narrower and the cell clips the checkbox.
const SELECT_COLUMN_WIDTH = 70;

/**
 * The name cell: a folder navigates into itself, an object is inert text.
 *
 * A link rather than a row click, so the folder can be opened in a new tab and reached by
 * keyboard, and so the Import button beside it stays independently clickable.
 */
function NameCell({
  entry,
  bucketName,
  prefix,
}: {
  entry: S3ObjectEntry;
  bucketName: string;
  prefix: string;
}) {
  const name = truncateMiddle(
    entryDisplayName(entry.key, prefix),
    NAME_DISPLAY_LIMIT,
  );

  if (entry.kind === "object") {
    return (
      <span className={styles.name} title={entry.key}>
        <InsertDriveFileOutlinedIcon fontSize="small" className={styles.icon} />
        <span>{name}</span>
      </span>
    );
  }

  return (
    <Link
      to={Goto.createS3BucketExplorerRoute(bucketName, entry.key)}
      className={styles.folderLink}
      title={entry.key}
    >
      <FolderOutlinedIcon fontSize="small" className={styles.icon} />
      <span>{name}</span>
    </Link>
  );
}

/**
 * The selection column.
 *
 * Hand-rolled rather than `DataTable`'s own, because that one is cleared whenever the page is
 * reset — which for this table happens every time the operator opens a folder, the one moment
 * the selection has to survive. Only objects are selectable: a folder is a prefix, and the
 * objects under it are selected by opening it.
 */
function selectionColumn(
  selectedKeys: ReadonlySet<string>,
  pageObjects: S3ObjectEntry[],
  onToggle: (entry: S3ObjectEntry, isSelected: boolean) => void,
  onTogglePage: (isSelected: boolean) => void,
): ColDef<S3ObjectEntry> {
  const selectedOnPage = pageObjects.filter((entry) =>
    selectedKeys.has(entry.key),
  ).length;

  return {
    colId: "select",
    width: SELECT_COLUMN_WIDTH,
    lockPosition: "left",
    suppressMovable: true,
    sortable: false,
    resizable: false,
    hide: false,
    lockVisible: true,
    headerComponent: () => (
      <Checkbox
        size="small"
        disabled={pageObjects.length === 0}
        checked={selectedOnPage > 0 && selectedOnPage === pageObjects.length}
        indeterminate={
          selectedOnPage > 0 && selectedOnPage < pageObjects.length
        }
        onChange={(event) => onTogglePage(event.target.checked)}
        inputProps={{ "aria-label": "Select every object on this page" }}
      />
    ),
    cellRenderer: (params: ICellRendererParams<S3ObjectEntry>) => {
      const entry = params.data;
      if (entry === undefined || entry.kind !== "object") {
        return null;
      }
      return (
        <Checkbox
          size="small"
          checked={selectedKeys.has(entry.key)}
          onChange={(event) => onToggle(entry, event.target.checked)}
          inputProps={{ "aria-label": `Select ${entry.key}` }}
        />
      );
    },
  };
}

/**
 * Columns declare `hide: false`, which is how a table with no saved preferences stays
 * ColDef-driven, and `sortable: false`, because S3 serves exactly one order: by key.
 */
function buildColumns(
  bucketName: string,
  prefix: string,
): ColDef<S3ObjectEntry>[] {
  return [
    columnHelper.accessor("key", {
      headerName: "Name",
      flex: 3,
      minWidth: 260,
      hide: false,
      sortable: false,
      cellClass: cellStyles.monoCell,
      cellRenderer: (params: ICellRendererParams<S3ObjectEntry>) =>
        params.data === undefined ? null : (
          <NameCell
            entry={params.data}
            bucketName={bucketName}
            prefix={prefix}
          />
        ),
    }),
    columnHelper.accessor("size", {
      headerName: "Size",
      width: 120,
      hide: false,
      sortable: false,
      // `renderDashOrValue` keys off the raw value, so a folder's absent size renders the shared
      // em-dash and never reaches this formatter.
      valueFormatter: (
        params: ValueFormatterParams<S3ObjectEntry, number | null | undefined>,
      ) => formatByteSize(params.value ?? 0),
      cellRenderer: renderDashOrValue,
    }),
    columnHelper.timestamp("last_modified", {
      headerName: "Last modified",
      width: 190,
      hide: false,
      sortable: false,
      variant: "precise",
    }),
    columnHelper.accessor("storage_class", {
      headerName: "Storage class",
      width: 150,
      hide: false,
      sortable: false,
      cellRenderer: renderDashOrValue,
    }),
  ];
}

interface LevelTableProps {
  table: DataTableState<S3ObjectEntry>;
  bucketName: string;
  prefix: string;
  rows: S3ObjectEntry[];
  loading: DataTableLoadingState;
  hasNextPage: boolean;
  /** Keys of every object selected so far, across folders. */
  selectedKeys: ReadonlySet<string>;
  onToggle: (entry: S3ObjectEntry, isSelected: boolean) => void;
  onTogglePage: (entries: S3ObjectEntry[], isSelected: boolean) => void;
}

/**
 * One level of the bucket: its folders and its objects in a single table, S3's own order.
 *
 * Objects carry a checkbox whose state comes from the page above, so a selection made here
 * survives opening another folder and comes back intact on the way out.
 *
 * No total row count, because S3 will not say how many keys a prefix holds without listing them
 * all. What a page contains is whatever that stretch of keys held, so a page can be mostly
 * folders, mostly objects, or a handful of either.
 */
export function LevelTable({
  table,
  bucketName,
  prefix,
  rows,
  loading,
  hasNextPage,
  selectedKeys,
  onToggle,
  onTogglePage,
}: LevelTableProps) {
  const pageObjects = React.useMemo(
    () => rows.filter((entry) => entry.kind === "object"),
    [rows],
  );

  const columns = React.useMemo(
    () => [
      selectionColumn(selectedKeys, pageObjects, onToggle, (isSelected) =>
        onTogglePage(pageObjects, isSelected),
      ),
      ...buildColumns(bucketName, prefix),
    ],
    [bucketName, prefix, selectedKeys, pageObjects, onToggle, onTogglePage],
  );

  return (
    <DataTable<S3ObjectEntry>
      table={table}
      hasNextPage={hasNextPage}
      loading={loading}
      columns={columns}
      data={rows}
      noRowsContent="This folder is empty."
    />
  );
}
